/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.21.0
 */

import { pageApi } from '@/main/api/page.api';
import { pageAccountInfoTags, userNotificationsTags } from '@/main/modules/app/apiTags';
import { EHttpMethod } from '@/main/modules/app/appConstants';
import { invalidateOnSuccess } from '@/main/utils/invalidateOnSuccess';
import { getNotificationsStart } from '@modules/notification/notificationActions';
import { ENotificationPlace } from '@modules/notification/types/notificationTypes';

import type {
    IMultiBalanceEventsQuery,
    IAccountInfoQuery,
    TDeliveryUpdateRequest,
    TClientId,
    TEdmDeliveryViewedRequest,
    TDeliveryUpdateInquiryRequest,
} from './types';
import type { IAccountDeliveryPageInfo, IAccountInfoGeneralMetaResponse } from './types/pageAccountInfoGeneralTypes';
import type {
    IAccountDeliveryInfoData,
    IAccountInfoFinancesDiscountsResponse,
    IAccountInfoFinancesGeneralMetaResponse,
    IAccountInfoMultiBalanceEventsResponse,
    IAccountInfoMultiBalanceListResponse,
    IDeliveryEmail,
    IUpdateEmailInquiry,
} from './types/pageAccountInfoTypes';
import { mapResponseToMeta } from './utils/transformAccountDeliveryMeta';

export const accountInfoApi = pageApi.injectEndpoints({
    endpoints: (builder) => ({
        getAccountInfoMeta: builder.query<IAccountInfoGeneralMetaResponse, IAccountInfoQuery>({
            query: ({ clientId }) => `account/info/${clientId}/general/meta`,
            async onQueryStarted({ clientId }, { dispatch }) {
                dispatch(
                    getNotificationsStart({
                        place: ENotificationPlace.accountId,
                        params: { clientId },
                    }),
                );
            },
        }),
        /* Finances */
        getAccountInfoFinancesMeta: builder.query<IAccountInfoFinancesGeneralMetaResponse, IAccountInfoQuery>({
            query: ({ clientId }) => `account/info/${clientId}/finances/meta`,
        }),
        getAccountInfoFinancesDiscounts: builder.query<IAccountInfoFinancesDiscountsResponse, IAccountInfoQuery>({
            query: ({ clientId }) => `account/info/${clientId}/finances/discounts`,
        }),
        getAccountInfoMultiBalanceList: builder.query<IAccountInfoMultiBalanceListResponse, IAccountInfoQuery>({
            query: ({ clientId }) => `account/info/${clientId}/finances/multi-balances`,
        }),
        getAccountInfoMultiBalanceExhaustedList: builder.query<IAccountInfoMultiBalanceListResponse, IAccountInfoQuery>(
            {
                query: ({ clientId }) => `account/info/${clientId}/finances/exhausted-multi-balances`,
            },
        ),
        getAccountInfoMultiBalanceEvents: builder.query<
            IAccountInfoMultiBalanceEventsResponse,
            IMultiBalanceEventsQuery
        >({
            query: ({ clientId, expandedBalanceId }) =>
                `account/info/${clientId}/finances/multi-balances/${expandedBalanceId}/events`,
        }),
        /* Delivery */
        getAccountInfoDeliveryMeta: builder.query<IAccountDeliveryInfoData, TClientId>({
            query: (clientId) => `account/info/${clientId}/delivery/meta`,
            transformResponse: (response: IAccountDeliveryPageInfo) => {
                console.log('xxx response?.deliveryInfoData', response);
                return mapResponseToMeta(response);
            },
            providesTags: [pageAccountInfoTags.ACCOUNT_INFO_DELIVERY_META],
        }),
        getAccountInfoDeliveryEmailInquiry: builder.query<IDeliveryEmail, TClientId>({
            query: (clientId) => `account/info/${clientId}/delivery/email/inquiry`,
        }),
        updateAccountInfoDelivery: builder.mutation<void, TDeliveryUpdateRequest>({
            query: ({ clientId, deliveryData }) => ({
                url: `account/info/${clientId}/delivery/update`,
                method: 'POST',
                body: deliveryData,
            }),
            invalidatesTags: [pageAccountInfoTags.ACCOUNT_INFO_DELIVERY_META],
        }),

        updateAccountInfoDeliveryInquiry: builder.mutation<IUpdateEmailInquiry, TDeliveryUpdateInquiryRequest>({
            query: ({ clientId, deliveryData }) => ({
                url: `account/info/${clientId}/delivery/update/inquiry`,
                method: 'POST',
                body: deliveryData,
            }),
            invalidatesTags: [pageAccountInfoTags.ACCOUNT_INFO_DELIVERY_META],
        }),

        sendEdmDeliveryViewed: builder.mutation<void, TEdmDeliveryViewedRequest>({
            query: ({ clientId }) => ({
                url: `account/info/${clientId}/delivery/viewed`,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
    }),
});

export const {
    useGetAccountInfoDeliveryMetaQuery,
    useGetAccountInfoDeliveryEmailInquiryQuery,
    useLazyGetAccountInfoDeliveryEmailInquiryQuery,
    useLazyGetAccountInfoDeliveryMetaQuery,
    useUpdateAccountInfoDeliveryMutation,
    useUpdateAccountInfoDeliveryInquiryMutation,
    useSendEdmDeliveryViewedMutation,
} = accountInfoApi;

export const useGetAccountInfoDeliveryMetaState = accountInfoApi.endpoints.getAccountInfoDeliveryMeta.useQueryState;
